import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SupportChatWidget from './support/SupportChatWidget';
import OnboardingWidget from './onboarding/OnboardingWidget';
import GuidedTour from './onboarding/GuidedTour';
import ContextualOnboardingTip from './onboarding/ContextualOnboardingTip';
import OnboardingWelcome from './onboarding/OnboardingWelcome';
import { CUSTOMIZABLE_MENU_ITEMS } from '../config/menuItems';
import { trackUxInteraction } from '../services/uxTelemetry';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  Package,
  Settings,
  LogOut,
  Menu,
  X,
  CreditCard,
  ClipboardList,
  ClipboardCheck,
  Brain,
  Image,
  DollarSign,
  FilePlus,
  FileCheck,
  Bell,
  HelpCircle,
  MessageCircle,
  Smile,
  UserCog,
  ShieldCheck,
  KeyRound
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clinicSettings, setClinicSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('clinicSettings');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const [whatsappUnreadCount, setWhatsappUnreadCount] = useState(0);
  const [whatsappAdminUnreadCount, setWhatsappAdminUnreadCount] = useState(0);
  const [anamnesePendingCount, setAnamnesePendingCount] = useState(0);
  const [switchingClinic, setSwitchingClinic] = useState(false);
  const lastTrackedModuleViewRef = useRef(null);

  const { user, logout, menuPreferences, menuPreferencesLoading, clinicContexts, selectedClinicId, clinicContextsLoading, canCreateOwnClinic, switchClinic } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:8001'
      : '');

  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${backendUrl}/api/clinic-settings`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('clinicSettings', JSON.stringify(data));
          setClinicSettings(data);
        } else {
          console.error('Failed to fetch clinic settings:', response.status);
        }
      } catch (error) {
        console.error('Error fetching clinic settings:', error);
      }
    };

    if (user) {
      fetchClinicSettings();
    }
  }, [user, backendUrl]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!clinicSettings) return;

    const primary = clinicSettings.primary_color || '#7c3aed';
    const secondary = clinicSettings.secondary_color || '#f3f4f6';

    document.documentElement.style.setProperty(
      '--clinic-primary',
      primary
    );

    document.documentElement.style.setProperty(
      '--clinic-secondary',
      secondary
    );
  }, [clinicSettings]);

  useEffect(() => {
    const hasWhatsAppModule =
      clinicSettings?.whatsapp_module_access_active === true ||
      clinicSettings?.whatsapp_module_status === 'active' ||
      clinicSettings?.whatsapp_module_status === 'courtesy' ||
      clinicSettings?.whatsapp_module_status === 'cancel_pending' ||
      clinicSettings?.whatsapp_module_enabled === true ||
      user?.whatsapp_module_enabled === true ||
      user?.whatsapp_module_courtesy === true;

    const fetchWhatsappUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token');

        if (!token || !hasWhatsAppModule) {
          setWhatsappUnreadCount(0);
          return;
        }

        const response = await fetch(`${backendUrl}/api/webhooks/whatsapp/chats`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) return;

        const chats = await response.json();

        const total = Array.isArray(chats)
          ? chats.reduce((sum, chat) => sum + Number(chat.unread_count || 0), 0)
          : 0;

        setWhatsappUnreadCount(total);
      } catch (error) {
        console.error('Erro ao carregar contador WhatsApp:', error);
      }
    };

    const fetchWhatsappAdminUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token');

        if (!token || user?.role !== 'admin') {
          setWhatsappAdminUnreadCount(0);
          return;
        }

        const response = await fetch(
          `${backendUrl}/api/webhooks/whatsapp/admin/chats`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) return;

        const chats = await response.json();

        const total = Array.isArray(chats)
          ? chats
              .filter((chat) => (chat.status || 'open') === 'open')
              .reduce(
                (sum, chat) => sum + Number(chat.unread_count || 0),
                0
              )
          : 0;

        setWhatsappAdminUnreadCount(total);
      } catch (error) {
        console.error(error);
      }
    };

    if (user) {
      fetchWhatsappUnreadCount();
      fetchWhatsappAdminUnreadCount();

      const interval = setInterval(() => {
        fetchWhatsappUnreadCount();
        fetchWhatsappAdminUnreadCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [user, clinicSettings, backendUrl]);

  useEffect(() => {
    const fetchAnamnesePendingCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const permissions = new Set(user?.permissions || []);
        if (!token || (permissions.size > 0 && !permissions.has('anamnese.view'))) {
          setAnamnesePendingCount(0);
          return;
        }

        const response = await fetch(
          `${backendUrl}/api/anamnese/responses/pending-count`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) return;

        const data = await response.json();
        setAnamnesePendingCount(Number(data?.count || 0));
      } catch (error) {
        console.error('Erro ao carregar contador de anamneses pendentes:', error);
      }
    };

    if (user) {
      fetchAnamnesePendingCount();
      const interval = setInterval(fetchAnamnesePendingCount, 30000);
      window.addEventListener(
        'anamnese:pending-count-changed',
        fetchAnamnesePendingCount
      );

      return () => {
        clearInterval(interval);
        window.removeEventListener(
          'anamnese:pending-count-changed',
          fetchAnamnesePendingCount
        );
      };
    }
  }, [user, backendUrl, selectedClinicId]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleClinicChange = async (event) => {
    const clinicId = event.target.value;
    if (clinicId === '__create_own_clinic__') {
      navigate('/criar-minha-clinica');
      return;
    }
    if (!clinicId || clinicId === selectedClinicId) return;
    try {
      setSwitchingClinic(true);
      await switchClinic(clinicId);
    } catch (error) {
      console.error('Erro ao trocar de clínica:', error);
      window.alert(error?.response?.data?.detail || 'Não foi possível trocar de clínica.');
      setSwitchingClinic(false);
    }
  };

  const hasWhatsAppModule =
    clinicSettings?.whatsapp_module_access_active === true ||
    clinicSettings?.whatsapp_module_status === 'active' ||
    clinicSettings?.whatsapp_module_status === 'courtesy' ||
    clinicSettings?.whatsapp_module_status === 'cancel_pending' ||
    clinicSettings?.whatsapp_module_enabled === true ||
    user?.whatsapp_module_enabled === true ||
    user?.whatsapp_module_courtesy === true;

  const userPermissions = Array.isArray(user?.permissions)
    ? new Set(user.permissions)
    : null;
  const can = (permission) => !userPermissions || userPermissions.has(permission);
  const menuPermission = {
    dashboard: 'dashboard.view', agenda: 'appointments.view',
    whatsapp_business: 'whatsapp.view', records: 'patients.view',
    aligners: 'aligners.view', reminders: 'appointments.view',
    anamnese: 'anamnese.view', ai_analysis: 'ai.clinical_use',
    clinical_documents: 'documents.view', contracts: 'contracts.view',
    compliance: 'compliance.view',
    inventory: 'inventory.view', billing: 'billing.view',
  };

  const menuIcons = {
    dashboard: LayoutDashboard,
    agenda: Calendar,
    whatsapp_business: MessageCircle,
    records: FileText,
    aligners: Smile,
    reminders: Bell,
    anamnese: ClipboardList,
    ai_analysis: Brain,
    clinical_documents: FilePlus,
    contracts: FileCheck,
    compliance: ClipboardCheck,
    inventory: Package,
    billing: DollarSign,
  };

  const customizableNavigation = CUSTOMIZABLE_MENU_ITEMS
    .filter((item) => can(menuPermission[item.id]))
    .filter((item) => item.id !== 'whatsapp_business' || hasWhatsAppModule)
    .map((item) => item.id === 'records' && !can('clinical_records.view')
      ? {
          ...item,
          name: 'Pacientes',
          description: 'Lista e dados cadastrais dos pacientes.',
          icon: menuIcons[item.id],
        }
      : { ...item, icon: menuIcons[item.id] });

  const canManageClinicUsers =
    can('settings.manage_users') && (
      user?.clinic_role === 'clinic_admin' ||
      (user?.role === 'user' && user?.id === user?.default_clinic_id)
    );

  const fixedNavigation = [
    ...(canManageClinicUsers ? [{ name: 'Usuários e permissões', href: '/usuarios', icon: UserCog }] : []),
    ...(can('audit.view') && user?.role !== 'admin' ? [{ name: 'Auditoria', href: '/auditoria', icon: ShieldCheck }] : []),
    { name: 'Segurança da conta', href: '/seguranca-da-conta', icon: KeyRound },
    ...(can('subscription.view') ? [{ name: 'Assinatura', href: '/assinatura', icon: CreditCard }] : []),
    { name: 'Central de Ajuda', href: '/ajuda', icon: HelpCircle },
    ...(can('settings.update_clinic') ? [{ name: 'Configurações', href: '/configuracoes', icon: Settings }] : []),
  ];

  const navigation = [
    ...customizableNavigation,
    ...fixedNavigation,
  ];

  const adminNavigation = user?.role === 'admin'
    ? [
        { name: 'Painel Admin', href: '/admin', icon: Settings, isAdmin: true },
        { name: 'WhatsApp Admin', href: '/admin/whatsapp', icon: MessageCircle, isAdmin: true },
      ]
    : [];

  const isInactiveUser =
    user?.role !== 'admin' &&
    ['inactive', 'blocked', 'suspended'].includes(user?.subscription_status);

  const subscriptionNavigation = isInactiveUser
    ? navigation.filter((item) => ['/assinatura', '/configuracoes', '/seguranca-da-conta'].includes(item.href))
    : navigation;

  const hiddenMenuItems = new Set(menuPreferences?.hidden_items || []);
  const visibleNavigation = menuPreferencesLoading
    ? []
    : subscriptionNavigation.filter((item) => !item.id || !hiddenMenuItems.has(item.id));
  const hiddenAvailableResources = menuPreferencesLoading
    ? []
    : customizableNavigation.filter((item) => hiddenMenuItems.has(item.id));
  const telemetryUserId = user?.id;
  const telemetryClinicContextId =
    selectedClinicId || user?.active_clinic_id || user?.default_clinic_id || 'current';

  useEffect(() => {
    if (!telemetryUserId || isInactiveUser) return;

    let viewedModule = CUSTOMIZABLE_MENU_ITEMS.find(
      (item) =>
        location.pathname === item.href ||
        location.pathname.startsWith(`${item.href}/`)
    );
    if (!viewedModule && location.pathname.startsWith('/pacientes/')) {
      viewedModule = CUSTOMIZABLE_MENU_ITEMS.find((item) => item.id === 'records');
    }
    if (!viewedModule) {
      lastTrackedModuleViewRef.current = null;
      return;
    }

    const viewKey = `${telemetryUserId}:${telemetryClinicContextId}:${location.pathname}`;
    if (lastTrackedModuleViewRef.current === viewKey) return;
    lastTrackedModuleViewRef.current = viewKey;

    trackUxInteraction({
      event_name: 'module_viewed',
      module_id: viewedModule.id,
      source: 'route',
    });
  }, [
    location.pathname,
    telemetryClinicContextId,
    telemetryUserId,
    isInactiveUser,
  ]);

  const currentPageTitle =
    navigation.find((item) => item.href === location.pathname)?.name ||
    adminNavigation.find((item) => item.href === location.pathname)?.name ||
    'OdontoAssist AI';
  const activeClinicName = clinicSettings?.clinic_name || user?.active_clinic_name;

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 bottom-0 left-0 z-30 w-72 max-w-[88vw] bg-white shadow-lg
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:w-64 lg:max-w-none
          overflow-y-auto overflow-x-hidden
        `}
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
        }}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center min-w-0">
            {clinicSettings?.logo_data ? (
              <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center mr-2 shrink-0">
                <img
                  src={`data:image/jpeg;base64,${clinicSettings.logo_data}`}
                  alt="Logo da Clínica"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center mr-2 shrink-0"
                style={{
                  backgroundColor:
                    clinicSettings?.primary_color || '#7c3aed',
                }}
              >
                <span className="text-white font-bold text-sm">OA</span>
              </div>
            )}

            <div className="flex flex-col min-w-0">
              {activeClinicName ? (
                <>
                  <span className="text-sm font-bold text-gray-800 truncate">
                    {activeClinicName}
                  </span>
                  {clinicContexts.length > 1 || canCreateOwnClinic ? <select aria-label="Clínica ativa" disabled={switchingClinic || clinicContextsLoading} value={selectedClinicId || user?.active_clinic_id || ''} onChange={handleClinicChange} className="mt-0.5 max-w-full text-xs text-gray-600 bg-transparent border-0 p-0 pr-5 focus:ring-0 cursor-pointer disabled:opacity-60">
                    {clinicContexts.map((context) => <option key={context.clinic_id} value={context.clinic_id} disabled={!context.selectable}>{context.clinic_name}{context.membership_status !== 'active' ? ' (indisponível)' : ''}</option>)}
                    {canCreateOwnClinic && <option value="__create_own_clinic__">＋ Criar minha clínica</option>}
                  </select> : <span className="text-xs text-gray-500 truncate">OdontoAssist AI</span>}
                </>
              ) : (
                <span className="text-xl font-bold text-gray-800 truncate">
                  OdontoAssist AI
                </span>
              )}
            </div>
          </div>

          <button onClick={() => setSidebarOpen(false)} className="lg:hidden shrink-0">
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="px-4 py-6">
          {visibleNavigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => {
                  if (item.id) {
                    trackUxInteraction({
                      event_name: 'module_opened',
                      module_id: item.id,
                      source: 'sidebar',
                    });
                  }
                }}
                className={`flex items-center px-3 py-2.5 mb-2 rounded-lg transition-colors ${
                  isActive
                    ? 'font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={
                  isActive
                    ? {
                        backgroundColor:
                          `${clinicSettings?.primary_color || '#7c3aed'}20`,
                        color:
                          clinicSettings?.primary_color || '#7c3aed',
                      }
                    : {}
                }
              >
                <Icon className="w-5 h-5 mr-3 shrink-0" />

                <span className="truncate">{item.name}</span>

                {item.href === '/whatsapp-business' && whatsappUnreadCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-green-600 text-white text-xs font-semibold">
                    {whatsappUnreadCount > 99 ? '99+' : whatsappUnreadCount}
                  </span>
                )}

                {item.href === '/anamnese' && anamnesePendingCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold">
                    {anamnesePendingCount > 99 ? '99+' : anamnesePendingCount}
                  </span>
                )}
              </Link>
            );
          })}

          {adminNavigation.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-3 mb-2">
                Administração
              </p>

              {adminNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2.5 mb-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-red-100 text-red-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3 shrink-0" />
                    <span className="truncate">{item.name}</span>
                    {item.href === '/admin/whatsapp' &&
                      whatsappAdminUnreadCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-xs font-semibold">
                          {whatsappAdminUnreadCount > 99
                            ? '99+'
                            : whatsappAdminUnreadCount}
                        </span>
                    )}                    
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        {!isInactiveUser && can('settings.update_clinic') && (
          <div className="px-4 pb-4">
            {hiddenAvailableResources.length > 0 ? (
              <Link
                to="/configuracoes?tab=menu"
                onClick={() => trackUxInteraction({
                  event_name: 'more_resources_opened',
                  source: 'more_resources',
                })}
                className="block rounded-lg border border-dashed border-purple-200 bg-purple-50 px-3 py-3 text-purple-700 transition hover:border-purple-300 hover:bg-purple-100"
              >
                <span className="flex items-center justify-between gap-3 text-sm font-semibold">
                  Mais recursos
                  <span className="inline-flex min-w-[22px] items-center justify-center rounded-full bg-purple-600 px-1.5 py-0.5 text-xs text-white">
                    {hiddenAvailableResources.length}
                  </span>
                </span>
                <span className="mt-1 block text-xs leading-5 text-purple-600">
                  Ative outros módulos quando precisar.
                </span>
              </Link>
            ) : (
              <Link
                to="/configuracoes?tab=menu"
                className="block px-3 text-xs text-gray-500 hover:text-purple-600"
              >
                Personalizar menu
              </Link>
            )}
          </div>
        )}

        <div className="px-4 py-3 border-t bg-white">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-3 mb-2">
            Institucional
          </p>

          <div className="grid grid-cols-2 gap-1 text-xs px-3">
            <Link to="/privacidade" className="text-gray-500 hover:text-purple-600">
              Privacidade
            </Link>
            <Link to="/termos-de-uso" className="text-gray-500 hover:text-purple-600">
              Termos
            </Link>
            <Link to="/lgpd" className="text-gray-500 hover:text-purple-600">
              LGPD
            </Link>
            <Link to="/seguranca" className="text-gray-500 hover:text-purple-600">
              Segurança
            </Link>
            <Link to="/status" className="text-gray-500 hover:text-purple-600 col-span-2">
              Status do Sistema
            </Link>
          </div>
        </div>

        <div className="px-4 py-4 border-t bg-white mt-auto">
          <div className="flex items-center mb-4 min-w-0">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                user?.role === 'admin' ? 'bg-red-100' : 'bg-gray-300'
              }`}
            >
              <span
                className={`font-medium text-sm ${
                  user?.role === 'admin' ? 'text-red-600' : 'text-gray-600'
                }`}
              >
                {user?.full_name?.charAt(0) || 'U'}
              </span>
            </div>

            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {user?.full_name || 'Usuário'}
                {user?.role === 'admin' && (
                  <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                    Admin
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center w-full px-3 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5 mr-3 shrink-0" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white shadow-sm border-b shrink-0">
          <div className="flex items-center justify-between px-4 py-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <Menu className="w-6 h-6" />
            </button>

            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">
              {currentPageTitle}
            </h1>

            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex items-center space-x-2 min-w-0">
                {clinicSettings?.logo_data ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center shrink-0">
                    <img
                      src={`data:image/jpeg;base64,${clinicSettings.logo_data}`}
                      alt="Logo da Clínica"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-gray-600 font-medium text-sm">
                      {user?.full_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                )}

                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {clinicSettings?.clinic_name || user?.full_name || 'Usuário'}
                  </span>
                  {clinicSettings?.clinic_name && (
                    <span className="text-xs text-gray-500 truncate">{user?.full_name}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main data-onboarding-id="onboarding-page-content" className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
          {children}
        </main>
      </div>

      <SupportChatWidget />
      <OnboardingWidget />
      <GuidedTour />
      <ContextualOnboardingTip />
      <OnboardingWelcome />
    </div>
  );
};

export default Layout;
